import { Sun, Moon, Monitor, PanelRight, PanelBottom, Square } from "lucide-react";
import { SegmentedControl } from "@dfws/ui";
import type { Density, ExternalImages, SplitView, ThemePreference } from "@dfws/contracts";
import { updateSettings, useSettings } from "../../settings/store";
import { AccentPicker } from "./AccentPicker";
import { Row } from "./parts";
import s from "./Settings.module.css";

export function GeneralSection() {
  const st = useSettings();
  return (
    <>
      <p className={s.lead}>Saved to your account, so every browser and phone you sign in on looks the same.</p>
      <section className={s.group} aria-labelledby="g-look">
        <h3 id="g-look" className={s.groupTitle}>Appearance</h3>
        <Row label="Theme" hint="Match system follows your device's light and dark setting.">
          <SegmentedControl<ThemePreference> label="Theme" value={st.theme} onChange={(theme) => updateSettings({ theme })}
            options={[{ value: "light", label: "Light", icon: <Sun /> }, { value: "dark", label: "Dark", icon: <Moon /> }, { value: "system", label: "Match system", icon: <Monitor /> }]} />
        </Row>
        <Row label="Accent color" hint="Used for buttons, selection, focus and unread marks, in light and dark. Purple is the DevFusion color.">
          <AccentPicker value={st.accent} onChange={(accent) => updateSettings({ accent })} />
        </Row>
        <Row label="Density" hint="How much fits on screen in the conversation list.">
          <SegmentedControl<Density> label="Density" value={st.density} onChange={(density) => updateSettings({ density })}
            options={[{ value: "comfortable", label: "Comfortable" }, { value: "cozy", label: "Cozy" }, { value: "compact", label: "Compact" }]} />
        </Row>
        <Row label="Reading pane" hint="Where an open conversation appears. Phones always use a single pane.">
          <SegmentedControl<SplitView> label="Reading pane" value={st.splitView} onChange={(splitView) => updateSettings({ splitView })}
            options={[{ value: "right", label: "Right", icon: <PanelRight /> }, { value: "bottom", label: "Below", icon: <PanelBottom /> }, { value: "none", label: "None", icon: <Square /> }]} />
        </Row>
      </section>
      <section className={s.group} aria-labelledby="g-send">
        <h3 id="g-send" className={s.groupTitle}>Writing and sending</h3>
        <Row label="Undo send" hint="How long you have to take a message back after pressing Send.">
          <SegmentedControl<string> label="Undo send" value={String(st.undoSendSeconds)} onChange={(v) => updateSettings({ undoSendSeconds: Number(v) })}
            options={[{ value: "5", label: "5 s" }, { value: "10", label: "10 s" }, { value: "20", label: "20 s" }, { value: "30", label: "30 s" }]} />
        </Row>
        <Row label="Default reply" hint="What r and the first reply button do when a message went to several people.">
          <SegmentedControl<"reply" | "replyAll"> label="Default reply" value={st.defaultReply} onChange={(defaultReply) => updateSettings({ defaultReply })}
            options={[{ value: "reply", label: "Reply" }, { value: "replyAll", label: "Reply all" }]} />
        </Row>
        <Row label="Conversation order" hint="Where the newest message sits in a conversation.">
          <SegmentedControl<"oldest" | "newest"> label="Conversation order" value={st.threadOrder} onChange={(threadOrder) => updateSettings({ threadOrder })}
            options={[{ value: "oldest", label: "Newest at the bottom" }, { value: "newest", label: "Newest on top" }]} />
        </Row>
      </section>
      <section className={s.group} aria-labelledby="g-mail">
        <h3 id="g-mail" className={s.groupTitle}>Reading mail</h3>
        <Row label="Images in messages" hint="Images load through Workspace, so senders never see your IP address. Ask first also hides open-tracking pixels until you choose to show images.">
          <SegmentedControl<ExternalImages> label="Images in messages" value={st.externalImages} onChange={(externalImages) => updateSettings({ externalImages })}
            options={[{ value: "always", label: "Always show" }, { value: "ask", label: "Ask first" }]} />
        </Row>
        <Row label="Chat notifications" hint="A desktop notification for new chat messages when that conversation is not on screen. Muted conversations never notify.">
          <input type="checkbox" role="switch" className={s.switch} aria-label="Chat notifications" checked={st.chatNotifications} onChange={(e) => updateSettings({ chatNotifications: e.target.checked })} />
        </Row>
        <Row label="Time zone" hint="Dates and times in mail and calendar.">
          <span className={s.muted}>Asia/Karachi (PKT)</span>
        </Row>
      </section>
    </>
  );
}
